import { useQuery, useInfiniteQuery, queryOptions, infiniteQueryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import { listActivities, getActivityStats } from '../../api/crmApi';

import { activitiesKeys } from './keys';

export const activitiesByDealOptions = (dealId) =>
	queryOptions({
		queryKey: activitiesKeys.byDeal(dealId),
		queryFn: async () => {
			const res = await listActivities({ dealId, pageSize: 100, orderBy: 'createdAt', orderDir: 'desc' });
			return res.activities ?? [];
		},
		staleTime: STALE_TIMES.FREQUENT,
	});

export function useActivitiesByDeal(dealId) {
	return useQuery({
		...activitiesByDealOptions(dealId),
		enabled: Boolean(dealId),
	});
}

/**
 * Fetches company-wide activities + deal-specific stage changes.
 * Merges both sources (deduplicated by id) to give the deal detail view
 * a complete timeline: all company communication + this deal's stage transitions.
 */
export function useActivitiesForDealView(companyId, dealId) {
	const companyQuery = useQuery({
		queryKey: activitiesKeys.byCompany(companyId),
		queryFn: async () => {
			const res = await listActivities({
				crmCompanyId: companyId,
				pageSize: 100,
				orderBy: 'createdAt',
				orderDir: 'desc',
			});
			return res.activities ?? [];
		},
		staleTime: STALE_TIMES.FREQUENT,
		enabled: Boolean(companyId),
	});

	const stageChangeQuery = useQuery({
		queryKey: [...activitiesKeys.byDeal(dealId), 'stage_changes'],
		queryFn: async () => {
			const res = await listActivities({
				dealId,
				activityType: 'stage_change',
				pageSize: 100,
				orderBy: 'createdAt',
				orderDir: 'desc',
			});
			return res.activities ?? [];
		},
		staleTime: STALE_TIMES.FREQUENT,
		enabled: Boolean(dealId),
	});

	// Merge both sources — company may have multiple deals, so deal-specific
	// stage changes might not appear in the company query
	const companyActs = companyQuery.data ?? [];
	const stageActs = stageChangeQuery.data ?? [];

	// Deduplicate by id, then sort descending by the displayed date.
	// The timeline shows `completedAt ?? createdAt` (when the activity happened),
	// so we sort by the same field to keep the order consistent with the UI.
	const map = new Map();
	for (const a of companyActs) map.set(a.id, a);
	for (const a of stageActs) map.set(a.id, a);
	const timelineDate = (a) => new Date(a.completedAt ?? a.createdAt).getTime();
	const merged = [...map.values()].sort((a, b) => timelineDate(b) - timelineDate(a));

	return {
		data: merged,
		isLoading: companyQuery.isLoading || stageChangeQuery.isLoading,
	};
}

export const activitiesListOptions = (filters = {}) =>
	queryOptions({
		queryKey: activitiesKeys.list(filters),
		queryFn: async () => listActivities(filters),
		staleTime: STALE_TIMES.FREQUENT,
	});

export function useActivitiesList(filters = {}, { enabled = true } = {}) {
	return useQuery({ ...activitiesListOptions(filters), enabled });
}

const ACTIVITIES_PAGE_SIZE = 25;

export const activitiesInfiniteOptions = (filters = {}) =>
	infiniteQueryOptions({
		queryKey: activitiesKeys.listInfinite(filters),
		queryFn: async ({ pageParam = 1 }) =>
			listActivities({ page: pageParam, pageSize: ACTIVITIES_PAGE_SIZE, ...filters }),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.currentPage < lastPage.totalPageCount ? lastPage.currentPage + 1 : undefined,
		staleTime: STALE_TIMES.FREQUENT,
	});

export function useActivitiesInfinite(filters = {}) {
	return useInfiniteQuery(activitiesInfiniteOptions(filters));
}

export function useActivityStats(filters = {}) {
	return useQuery({
		queryKey: activitiesKeys.stats(filters),
		queryFn: () => getActivityStats(filters),
		staleTime: STALE_TIMES.FREQUENT,
	});
}
